import { ProviderAccessMap } from '@vgtv/svp-access/lib/access_mappings';
import type {
  Asset,
  AssetAdditional,
  AssetImages,
  AssetStreamConfiguration,
  Nullable,
} from '@schibsted-svp/svp-api-types';
import type { CnpTag } from '@vgtv/api-client/lib/cnp_tag';
import type { Player } from '@schibsted-svp/web-player';

import ServerTime from '../serverTime';
import { Unpacked } from '../../plugins/skin-vgtv2/utils/types';

import { getCategoryShortPreviewUrl } from './category';
import type { StreamProvider } from '@vgtv/api-client';

type VideoPreview = {
  aspectRatio: string;
  height: number;
  url: string;
  width: number;
};

export const getTimestamp = (
  time?: Nullable<number>,
  useMs: boolean = false
) => {
  if (!time) {
    return undefined;
  }

  const isTimeInSeconds = time.toString().length <= 10;
  if (isTimeInSeconds) {
    return useMs ? time * 1000 : time;
  }
  return useMs ? time : Math.floor(time / 1000);
};

export const getAssetFlightTimesStart = (
  asset: {
    flightTimes?: {
      start?: Asset['flightTimes']['start'];
    };
  },
  useMs: boolean = false
): number | undefined => {
  const { flightTimes: { start } = {} } = asset;

  return getTimestamp(start, useMs) ?? undefined;
};

export const getAssetFlightTimesEnd = (
  asset: {
    flightTimes?: {
      end?: Asset['flightTimes']['end'];
    };
  },
  useMs: boolean = false
): number | undefined => {
  const { flightTimes: { end } = {} } = asset;

  return getTimestamp(end, useMs) ?? undefined;
};

export const getAssetAccess = <T extends StreamProvider>(
  asset?: Asset
): ProviderAccessMap[T] | undefined => {
  return (
    Object.keys(asset?.additional?.access ?? {}) as ProviderAccessMap[T][]
  )[0];
};

export type VariantAccessLevel = 'plus' | 'login';

export const getAccessLevelFromVariantType = (
  variantType: string | undefined,
  provider: StreamProvider | 'brandstudio'
): VariantAccessLevel | undefined => {
  if (variantType === 'Subscription') {
    switch (provider) {
      case 'ab':
      case 'vgtv':
      case 'svd':
      case 'e24':
      case 'ap':
      case 'bt':
      case 'sa':
        return 'plus';
      default:
        return undefined;
    }
  }
  if (variantType === 'Login') {
    return 'login';
  }
  return undefined;
};

export const getPodcastAccessLevel = (
  asset: Asset | undefined,
  provider: StreamProvider | 'brandstudio'
): ProviderAccessMap[StreamProvider] | VariantAccessLevel | undefined =>
  getAssetAccess(asset) ??
  getAccessLevelFromVariantType(
    asset?.additional?.metadata?.['1variantType'],
    provider
  );

export function isSportAccessLevel<T extends StreamProvider>(
  provider: StreamProvider | 'brandstudio',
  accessLevel: ProviderAccessMap[T] | VariantAccessLevel | undefined
): accessLevel is Extract<ProviderAccessMap[T], 'sport' | 'plus'> {
  if (provider === 'ab') {
    return accessLevel === 'plus';
  }

  if (provider === 'vgtv') {
    return accessLevel === 'sport';
  }

  return false;
}

export const isSubscriptionAccessLevel = <T extends StreamProvider>(
  provider: StreamProvider | 'brandstudio',
  accessLevel: ProviderAccessMap[T] | VariantAccessLevel | undefined
): accessLevel is Extract<ProviderAccessMap[T], 'sport' | 'plus'> => {
  if (provider === 'ab') {
    return accessLevel === 'plus';
  }

  if (provider === 'vgtv') {
    return accessLevel === 'plus' || accessLevel === 'sport';
  }

  if (provider === 'svd') {
    return accessLevel === 'plus';
  }

  if (provider === 'e24') {
    return accessLevel === 'plus' || accessLevel === 'pluspro';
  }

  if (provider === 'ap') {
    return accessLevel === 'plus';
  }

  if (provider === 'bt') {
    return accessLevel === 'plus';
  }

  if (provider === 'sa') {
    return accessLevel === 'plus';
  }

  return false;
};

export const hasStreamProperty = (
  asset: Asset | undefined,
  property: Unpacked<AssetStreamConfiguration['properties']>
) => asset?.streamConfiguration.properties.includes(property) ?? false;

export const isLiveStream = (asset: { streamType: Asset['streamType'] }) =>
  asset.streamType === 'live';

export const getStreamStartDate = (asset: Asset) => {
  const flightTimesStart = getAssetFlightTimesStart(asset, true);

  return new Date(flightTimesStart!);
};

export const getTagIds = (asset?: Asset) => {
  return (asset?.additional?.tags ?? []) as CnpTag['id'][];
};

export const getTimeToStart = async (asset: Asset) => {
  const serverTime = await ServerTime.get();

  return getStreamStartDate(asset).getTime() - serverTime;
};

type AssetLikeWithMetadata = {
  additional?: Nullable<{ metadata?: AssetAdditional['metadata'] }>;
};

const defaultAssetMetadata: AssetAdditional['metadata'] = {};
Object.freeze(defaultAssetMetadata);
export const getAssetMetadata = (asset?: AssetLikeWithMetadata) => {
  return asset?.additional?.metadata ?? defaultAssetMetadata;
};

/**
 * Returns a numerical aspect ratio value.
 */
const DEFAULT_ASPECT_RATIO = 16 / 9;
export const getAssetAspectRatio = (asset?: AssetLikeWithMetadata): number => {
  const metadata = getAssetMetadata(asset);

  return parseFloat(
    (parseFloat(metadata.aspectRatio) || DEFAULT_ASPECT_RATIO).toFixed(2)
  );
};

const parsePreviewsFromProperty = (str: string) => {
  if (str) {
    try {
      return JSON.parse(str) as VideoPreview[][];
    } catch {
      // noop
    }
  }
  return undefined;
};

export const getAssetPreviewUrl = (asset?: Asset) => {
  const {
    livepreview,
    preview_vivi_category: viviPreview,
    preview,
    preview_portrait: portraitStr,
    preview_hq: previewHq,
  } = getAssetMetadata(asset);

  let previewUrl =
    previewHq ||
    viviPreview ||
    preview ||
    livepreview ||
    getCategoryShortPreviewUrl(asset?.category);

  if (asset?.streamType === 'live') {
    previewUrl = livepreview || previewUrl;
  }

  if (getAssetAspectRatio(asset) < 1) {
    const verticalPreviews = parsePreviewsFromProperty(portraitStr);
    previewUrl = verticalPreviews?.[0]?.[0]?.url || previewUrl;
  }

  return previewUrl;
};

const defaultTags: {
  id: CnpTag['id'];
  tag: string;
}[] = [];
Object.freeze(defaultTags);
export const getAssetEmbeddedTags = (asset: Asset) =>
  asset._embedded?.tags ?? defaultTags;

export const getAssetImage = <T extends keyof AssetImages>(
  asset: Asset,
  imageName: T
): AssetImages[T] => {
  let img = asset.images[imageName];
  if (img) {
    img = img.replace(/^http:/, 'https:');
  }
  return img;
};

export const getRawAsset = (player?: Player): Asset | undefined => {
  const asset = player?.getRawAsset();
  // Make sure we skip mock asset and not-yet-fetched assets
  // @ts-expect-error mock stream type does not exist in types
  if (asset && (!asset.title || asset.streamType === 'mock')) {
    return undefined;
  }

  return asset;
};
